import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { Messages } from '../i18n';
import {
    endpoints,
    type ConnectInvoiceBody,
    type ConnectPaymentBody,
    type CreateAnnouncementBody,
    type CreatePlanBody,
    type GrantStaffBody,
    type InvoiceIssuer,
    type PlanPreviewBody,
    type SetBankDetailsBody,
    type SetCardServiceBody,
    type SetFeatureBody,
    type SetInvoiceServiceBody,
    type SetPlanBody,
    type TenantStatus,
    type TrialExtensionBody,
    type UpdatePlanBody,
    type UpdateRequestBody,
    type VatBasis,
} from './api';
import { usePermittedMutation } from './session';

/** Platform data hooks. */

export function useSummary() {
    return useQuery({ queryKey: ['summary'], queryFn: endpoints.summary });
}

export function useBillingMetrics() {
    return useQuery({
        queryKey: ['billing-metrics'],
        queryFn: endpoints.billingMetrics,
    });
}

export function useTenantFeatures(tenantId: string | null) {
    return useQuery({
        queryKey: ['tenant-features', tenantId],
        queryFn: () => endpoints.tenantFeatures(tenantId as string),
        enabled: tenantId !== null,
    });
}

export function useSetTenantFeature(tenantId: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (body: SetFeatureBody) => endpoints.setTenantFeature(tenantId, body),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: ['tenant-features', tenantId],
            }),
    });
}

/** Hands an account's level for a feature back to its plan. */
export function useClearTenantFeature(tenantId: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (feature: string) => endpoints.clearTenantFeature(tenantId, feature),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant-features', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
        },
    });
}

export function useSetTenantPlan(tenantId: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (body: SetPlanBody) => endpoints.setTenantPlan(tenantId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['tenant-usage', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
            queryClient.invalidateQueries({ queryKey: ['billing-metrics'] });
            // A package or status set here is what puts an account on Genel Bakış'
            // lapsed and over-limit lists, or takes it off.
            queryClient.invalidateQueries({ queryKey: ['accounts-lapsed'] });
            queryClient.invalidateQueries({ queryKey: ['accounts-over-limit'] });
        },
    });
}

/**
 * An account's usage counted as billing counts it, beside its package's limits
 * and every package staff could move it to (ADR 0017 §13).
 */
export function useTenantUsage(tenantId: string) {
    return useQuery({
        queryKey: ['tenant-usage', tenantId],
        queryFn: () => endpoints.tenantUsage(tenantId),
    });
}

/** Lengthens a running trial; the reason goes to the audit log. */
export function useExtendTrial(tenantId: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (body: TrialExtensionBody) => endpoints.extendTrial(tenantId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
        },
    });
}

/** Every account above a limit its package sets: products, channels, users, and orders where a limit is set. */
export function useOverLimitAccounts() {
    return useQuery({
        queryKey: ['accounts-over-limit'],
        queryFn: endpoints.overLimitAccounts,
        // Sellers cross a limit by working, with no staff action to invalidate
        // this; support leaves Genel Bakış open and acts on what it shows.
        refetchInterval: 60_000,
    });
}

/** The accounts operating safely after an unpaid period, longest first (ADR 0017 §4). */
export function useLapsedAccounts() {
    return useQuery({
        queryKey: ['accounts-lapsed'],
        queryFn: endpoints.lapsedAccounts,
        // The hourly billing job lapses accounts with no staff action to
        // invalidate this; the list is the call sheet support works from.
        refetchInterval: 60_000,
    });
}

/**
 * A package's card as it is being typed, from the API: its prices on the
 * platform's VAT basis and what it includes (ADR 0017 §9–10). The typing is
 * let settle first, so a price typed digit by digit asks once, and the last
 * card stays in view while the next is on its way.
 */
export function usePlanPreview(body: PlanPreviewBody | null) {
    const [settled, setSettled] = useState(body);
    const key = JSON.stringify(body);
    useEffect(() => {
        const timer = window.setTimeout(() => setSettled(JSON.parse(key) as PlanPreviewBody | null), 300);
        return () => window.clearTimeout(timer);
    }, [key]);
    return useQuery({
        queryKey: ['plan-preview', settled],
        queryFn: () => endpoints.previewPlan(settled as PlanPreviewBody),
        enabled: settled !== null,
        placeholderData: (previous) => previous,
    });
}

/** Whether package prices include VAT (ADR 0017 §10). */
export function usePriceBasis() {
    return useQuery({ queryKey: ['price-basis'], queryFn: endpoints.priceBasis });
}

// What every seller is charged is the platform's commercial shape, as its
// packages are; every price shown follows it.
export function useSetPriceBasis() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (basis: VatBasis) => endpoints.setPriceBasis(basis),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['price-basis'] });
            queryClient.invalidateQueries({ queryKey: ['plan-preview'] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
        },
    });
}

export function useTenants(params: { search: string; status: string; limit: number; offset: number }) {
    return useQuery({
        queryKey: ['tenants', params],
        queryFn: () => endpoints.tenants(params),
        placeholderData: (previous) => previous,
    });
}

export function useTenantDetail(tenantId: string) {
    return useQuery({
        queryKey: ['tenant', tenantId],
        queryFn: () => endpoints.tenantDetail(tenantId),
    });
}

export function useTenantUsers(tenantId: string) {
    return useQuery({
        queryKey: ['tenant-users', tenantId],
        queryFn: () => endpoints.tenantUsers(tenantId),
    });
}

/** Who in an account accepted which versions of the terms, newest first (ADR 0018 §1). */
export function useTenantTermsAcceptances(tenantId: string) {
    return useQuery({
        queryKey: ['tenant-terms-acceptances', tenantId],
        queryFn: () => endpoints.tenantTermsAcceptances(tenantId),
    });
}

export function useTenantChannels(tenantId: string) {
    return useQuery({
        queryKey: ['tenant-channels', tenantId],
        queryFn: () => endpoints.tenantChannels(tenantId),
    });
}

export function useTenantHandoffs(tenantId: string) {
    return useQuery({
        queryKey: ['tenant-handoffs', tenantId],
        queryFn: () => endpoints.tenantHandoffs(tenantId),
    });
}

export function useSetTenantStatus(tenantId: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (status: Exclude<TenantStatus, 'trial'>) => endpoints.setTenantStatus(tenantId, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
        },
    });
}

export function useUsers(params: { search: string; status: string; limit: number; offset: number }) {
    return useQuery({
        queryKey: ['users', params],
        queryFn: () => endpoints.users(params),
        placeholderData: (previous) => previous,
    });
}

export function useSetUserStatus() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: ({ userId, status }: { userId: string; status: 'active' | 'disabled' }) => endpoints.setUserStatus(userId, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
        },
    });
}

/** Turns off a person's two-step sign-in, for a lost phone. */
export function useResetUserTwoFactor() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (userId: string) => endpoints.resetUserTwoFactor(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['audit'] });
        },
    });
}

export function useAudit(params: { tenantId?: string; search: string; actions?: string[]; limit: number; offset: number }) {
    return useQuery({
        queryKey: ['audit', params],
        queryFn: () => endpoints.audit(params),
        placeholderData: (previous) => previous,
    });
}

export function useOps() {
    return useQuery({
        queryKey: ['ops'],
        queryFn: endpoints.ops,
        // The person on duty leaves this open; a queue that stalled a minute ago
        // is the one they are about to hear about.
        refetchInterval: 30_000,
    });
}

export function usePlans() {
    return useQuery({ queryKey: ['plans'], queryFn: endpoints.plans });
}

export function useUpdatePlan() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: ({ key, body }: { key: string; body: UpdatePlanBody }) => endpoints.updatePlan(key, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['plans'] });
            queryClient.invalidateQueries({ queryKey: ['billing-metrics'] });
            queryClient.invalidateQueries({ queryKey: ['tenant-usage'] });
            // A limit edited here moves accounts across it.
            queryClient.invalidateQueries({ queryKey: ['accounts-over-limit'] });
        },
    });
}

export function useCreatePlan() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: CreatePlanBody) => endpoints.createPlan(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['plans'] });
            queryClient.invalidateQueries({ queryKey: ['billing-metrics'] });
        },
    });
}

export function useCharges(params: { status: string; limit: number; offset: number }) {
    return useQuery({
        queryKey: ['charges', params],
        queryFn: () => endpoints.charges(params),
        placeholderData: (previous) => previous,
    });
}

// A decided payment can move an account onto another plan, or out of a lapse,
// so what the revenue, the account pages and Genel Bakış' lists show changes
// with it.
function invalidateCharges(queryClient: QueryClient) {
    queryClient.invalidateQueries({ queryKey: ['charges'] });
    queryClient.invalidateQueries({ queryKey: ['billing-metrics'] });
    queryClient.invalidateQueries({ queryKey: ['tenant'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-usage'] });
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    queryClient.invalidateQueries({ queryKey: ['accounts-lapsed'] });
    queryClient.invalidateQueries({ queryKey: ['accounts-over-limit'] });
}

export function useApproveCharge() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (chargeId: string) => endpoints.approveCharge(chargeId),
        onSuccess: () => invalidateCharges(queryClient),
    });
}

// Applying a held payment moves an account onto what it paid for, as
// approving one does (ADR 0017 §8).
export function useApplyCharge() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (chargeId: string) => endpoints.applyCharge(chargeId),
        onSuccess: () => invalidateCharges(queryClient),
    });
}

export function useCancelCharge() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: ({ chargeId, note }: { chargeId: string; note: string }) => endpoints.cancelCharge(chargeId, note),
        onSuccess: () => invalidateCharges(queryClient),
    });
}

// Money going back is the platform's commercial decision.
export function useRefundCharge() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: ({ chargeId, note }: { chargeId: string; note: string }) => endpoints.refundCharge(chargeId, note),
        onSuccess: () => invalidateCharges(queryClient),
    });
}

export function useBankDetails() {
    return useQuery({ queryKey: ['bank-details'], queryFn: endpoints.bankDetails });
}

export function useSetBankDetails() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: SetBankDetailsBody) => endpoints.setBankDetails(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bank-details'] }),
    });
}

export function usePaymentProviders() {
    return useQuery({ queryKey: ['payment-providers'], queryFn: endpoints.paymentProviders });
}

export function usePaymentAccounts() {
    return useQuery({ queryKey: ['payment-accounts'], queryFn: endpoints.paymentAccounts });
}

// Connecting, choosing and withdrawing a service is how the platform is paid,
// its commercial shape; checking one is admin work.
export function useConnectPayment() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: ConnectPaymentBody) => endpoints.connectPayment(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-accounts'] }),
    });
}

export function useVerifyPayment() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (accountId: string) => endpoints.verifyPayment(accountId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-accounts'] }),
    });
}

export function useWithdrawPayment() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (accountId: string) => endpoints.withdrawPayment(accountId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-accounts'] }),
    });
}

export function useSetCardService() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: SetCardServiceBody) => endpoints.setCardService(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-accounts'] }),
    });
}

// The platform's invoices (ADR 0016 §9). In whose name and through which
// service they are filed is the platform's legal and commercial shape, as how
// it is paid is; checking a connection and seeing a charge's document through
// are admin work.
export function useInvoiceSettings() {
    return useQuery({ queryKey: ['invoice-settings'], queryFn: endpoints.invoiceSettings });
}

export function useSetInvoiceIssuer() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: InvoiceIssuer) => endpoints.setInvoiceIssuer(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice-settings'] }),
    });
}

export function useInvoiceProviders() {
    return useQuery({ queryKey: ['invoice-providers'], queryFn: endpoints.invoiceProviders });
}

export function useInvoiceAccounts() {
    return useQuery({ queryKey: ['invoice-accounts'], queryFn: endpoints.invoiceAccounts });
}

// Whether a connection is chosen and checked is what the settings report as
// ready, so both are read again after any change to a connection.
function invalidateInvoiceAccounts(queryClient: QueryClient) {
    queryClient.invalidateQueries({ queryKey: ['invoice-accounts'] });
    queryClient.invalidateQueries({ queryKey: ['invoice-settings'] });
}

export function useConnectInvoice() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: ConnectInvoiceBody) => endpoints.connectInvoice(body),
        onSuccess: () => invalidateInvoiceAccounts(queryClient),
    });
}

export function useVerifyInvoice() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (accountId: string) => endpoints.verifyInvoice(accountId),
        onSuccess: () => invalidateInvoiceAccounts(queryClient),
    });
}

export function useWithdrawInvoice() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (accountId: string) => endpoints.withdrawInvoice(accountId),
        onSuccess: () => invalidateInvoiceAccounts(queryClient),
    });
}

export function useSetInvoiceService() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: SetInvoiceServiceBody) => endpoints.setInvoiceService(body),
        onSuccess: () => invalidateInvoiceAccounts(queryClient),
    });
}

/** The e-invoice services sellers may be offered, and staff's decision on each (ADR 0008 point 17). */
export function useInvoiceOffers() {
    return useQuery({ queryKey: ['invoice-offers'], queryFn: endpoints.invoiceOffers });
}

export function useSetInvoiceOffer() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: ({ providerKey, offered }: { providerKey: string; offered: boolean }) => endpoints.setInvoiceOffer(providerKey, offered),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice-offers'] }),
    });
}

export function useIssueOpenInvoices() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: () => endpoints.issueOpenInvoices(),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['charges'] }),
    });
}

export function useRetryChargeInvoice() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (chargeId: string) => endpoints.retryChargeInvoice(chargeId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['charges'] }),
    });
}

/** What a charge was bought on, read when staff open it (ADR 0018 §5). */
export function useChargeAgreement(chargeId: string) {
    return useQuery({
        queryKey: ['charge-agreement', chargeId],
        queryFn: () => endpoints.chargeAgreement(chargeId),
    });
}

/**
 * Opens a charge's invoice. Fetched when pressed rather than rendered as a
 * link: the address is signed for an hour, and one minted with the page would
 * be dead by the time anyone pressed it.
 */
export async function openChargeInvoice(chargeId: string) {
    const link = await endpoints.chargeInvoiceLink(chargeId);
    window.open(link.url, '_blank', 'noopener');
}

export function useRecordChargeInvoiceSerial() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: ({ chargeId, serial }: { chargeId: string; serial: string }) => endpoints.recordChargeInvoiceSerial(chargeId, serial),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['charges'] }),
    });
}

export function useRequests(params: { status: string; kind: string; limit: number; offset: number }) {
    return useQuery({
        queryKey: ['requests', params],
        queryFn: () => endpoints.requests(params),
        placeholderData: (previous) => previous,
    });
}

export function useOpenRequests() {
    return useQuery({
        queryKey: ['requests-open'],
        queryFn: endpoints.openRequests,
        // The badge in the menu: a new message should show up while the operator
        // is on another screen.
        refetchInterval: 60_000,
    });
}

export function useUpdateRequest() {
    const queryClient = useQueryClient();
    return usePermittedMutation('support', {
        mutationFn: ({ id, body }: { id: string; body: UpdateRequestBody }) => endpoints.updateRequest(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['requests'] });
            queryClient.invalidateQueries({ queryKey: ['requests-open'] });
        },
    });
}

export function useFailingChannels() {
    return useQuery({
        queryKey: ['failing-channels'],
        queryFn: endpoints.failingChannels,
        // Support watches this screen while they work; a connection that broke
        // five minutes ago is the one they are about to be phoned about.
        refetchInterval: 60_000,
    });
}

export function useStaff() {
    return useQuery({ queryKey: ['staff'], queryFn: endpoints.staff });
}

export function useGrantStaff() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (body: GrantStaffBody) => endpoints.grantStaff(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
    });
}

export function useRevokeStaff() {
    const queryClient = useQueryClient();
    return usePermittedMutation('superadmin', {
        mutationFn: (userId: string) => endpoints.revokeStaff(userId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
    });
}

export function useAnnouncements() {
    return useQuery({
        queryKey: ['announcements'],
        queryFn: endpoints.announcements,
    });
}

export function useCreateAnnouncement() {
    const queryClient = useQueryClient();
    return usePermittedMutation('admin', {
        mutationFn: (body: CreateAnnouncementBody) => endpoints.createAnnouncement(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['announcements'] }),
    });
}

export function useSupportHandoff() {
    return usePermittedMutation('admin', {
        mutationFn: (tenantId: string) => endpoints.supportHandoff(tenantId),
        onSuccess: (result) => {
            // The token is single-use and lives for a minute, so it is redeemed
            // immediately rather than stored anywhere. A full navigation, because the
            // destination is a different application on a different origin.
            window.location.assign(result.redeemUrl);
        },
    });
}

/**
 * A connection's stored failure, as staff read it.
 *
 * The connection module stores a failure by its class wherever the channel's
 * own words would not help, and keeps those words only for a refusal they
 * explain (channel.SellerMessage). A class is written out here; anything else
 * is that explanation and is shown as it is.
 */
export function channelDetailText(t: Messages, detail: string): string {
    const sentence: string | undefined = t.accountDetail.channelProblem[detail as keyof typeof t.accountDetail.channelProblem];
    return sentence ?? detail;
}
